import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Pencil, Plus, Search } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import useJamoraCatalog from "../hooks/useJamoraCatalog";
import {
  TRAINING_MANAGE_CATALOG, buildFeaturePatch, copyLabel, filterFeatures, groupBySection, isPatchEmpty, roleLabels,
} from "../lib/training";
import { LoadError, SectionHeader } from "./Settings";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ALL = "__all__";
const STATUS_OPTIONS = [
  { value: "active", label: "Actives" },
  { value: "retired", label: "Retirées" },
  { value: ALL, label: "Toutes" },
];
const SOURCE_OPTIONS = [
  { value: ALL, label: "Toutes origines" },
  { value: "jamora", label: "Copiées de Jamora" },
  { value: "crm", label: "Ajoutées dans le CRM" },
];

function Tag({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    jamora: "bg-jamora/10 text-jamora",
    amber: "bg-amber-50 text-amber-800",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium ${tones[tone]}`}>{children}</span>;
}

// Paramètres › Fonctionnalités Jamora (Ordre 11-1): the features of Jamora's software, copied from Jamora's code, on which skills, and later
// modules and programmes, rely. Read with a training or missions key; changed with training:manage_catalog (the server checks both).
export default function SettingsTrainingCatalog() {
  const { user } = useAuth();
  const catalog = useJamoraCatalog(true);
  const canManage = catalog.can_manage && hasPermission(user, TRAINING_MANAGE_CATALOG);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [source, setSource] = useState(ALL);
  const [role, setRole] = useState(ALL);
  const [opened, setOpened] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // null | "new" | feature
  const [renaming, setRenaming] = useState(null);

  const filtered = useMemo(() => filterFeatures(catalog.features, {
    search, status: status === ALL ? "" : status, source: source === ALL ? "" : source, role: role === ALL ? "" : role,
  }), [catalog.features, search, status, source, role]);
  const groups = useMemo(() => groupBySection(catalog.sections, filtered), [catalog.sections, filtered]);
  const narrowed = search.trim() !== "" || source !== ALL || role !== ALL || status !== "active";
  const isOpen = (code) => narrowed || opened.has(code);
  const toggle = (code) => setOpened((prev) => {
    const next = new Set(prev);
    next.has(code) ? next.delete(code) : next.add(code);
    return next;
  });
  const missing = catalog.features.filter((f) => f.missing_from_copy && f.status === "active").length;

  if (catalog.status === "error") return <LoadError onRetry={catalog.reload} />;

  return (
    <div className="space-y-4" data-testid="settings-training-catalog">
      <SectionHeader title="Fonctionnalités Jamora"
        description="Les fonctionnalités du logiciel Jamora. Les compétences, puis les modules et programmes de formation, s'appuient sur elles." />

      {catalog.status === "loading" ? (
        <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-64 w-full" /></div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500" data-testid="catalog-copy">
              {copyLabel(catalog.copy)} · {catalog.features.filter((f) => f.status === "active").length} fonctionnalités actives, {catalog.sections.length} rubriques
            </p>
            {canManage && (
              <Button onClick={() => setEditing("new")} data-testid="btn-feature-new">
                <Plus size={16} className="mr-1.5" /> Nouvelle fonctionnalité
              </Button>
            )}
          </div>

          {missing > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="catalog-missing">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                {missing === 1 ? "Une fonctionnalité n'existe plus" : `${missing} fonctionnalités n'existent plus`} dans la dernière copie de Jamora.
                Elle{missing === 1 ? " reste" : "s restent"} ici pour l'historique : retirez-la{missing === 1 ? "" : "s"} si Jamora ne la propose plus.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_10rem_11rem_12rem] gap-2">
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une fonctionnalité ou un code (ex. pos.sale)"
                className="pl-9 text-base md:text-sm" aria-label="Rechercher dans le catalogue" data-testid="catalog-search" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger aria-label="État" data-testid="catalog-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger aria-label="Rôle pharmacie" data-testid="catalog-filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les rôles</SelectItem>
                {catalog.roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger aria-label="Origine" data-testid="catalog-filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {groups.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-2" data-testid="catalog-empty">
              <p className="font-medium text-slate-900">Aucune fonctionnalité ne correspond</p>
              <p className="text-sm text-slate-500">Essayez un autre mot, ou affichez toutes les fonctionnalités.</p>
              <Button variant="outline" size="sm" onClick={() => { setSearch(""); setStatus(ALL); setSource(ALL); setRole(ALL); }}>Tout afficher</Button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
              {groups.map(({ section, features }) => (
                <div key={section.code} data-testid={`catalog-section-${section.code}`}>
                  <div className="flex items-center gap-1 pr-2">
                    <button type="button" onClick={() => toggle(section.code)} aria-expanded={isOpen(section.code)}
                      className="flex-1 min-w-0 flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora/40"
                      data-testid={`btn-section-toggle-${section.code}`}>
                      {isOpen(section.code) ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
                      <span className="font-medium text-slate-900 truncate">{section.label}</span>
                      <span className="text-xs text-slate-500 shrink-0">{features.length}</span>
                    </button>
                    {canManage && (
                      <Button variant="ghost" size="icon" aria-label={`Renommer la rubrique ${section.label}`} onClick={() => setRenaming(section)}
                        data-testid={`btn-section-rename-${section.code}`}><Pencil size={15} /></Button>
                    )}
                  </div>
                  {isOpen(section.code) && (
                    <ul className="pb-2">
                      {features.map((f) => (
                        <li key={f.code}>
                          <button type="button" onClick={() => setEditing(f)}
                            className="w-full text-left px-4 pl-10 py-2 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora/40"
                            data-testid={`feature-row-${f.code}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm ${f.status === "retired" ? "text-slate-400 line-through" : "text-slate-800"}`}>{f.label}</span>
                              {f.source === "crm" && <Tag tone="jamora">Ajoutée dans le CRM</Tag>}
                              {f.status === "retired" && <Tag>Retirée</Tag>}
                              {f.missing_from_copy && <Tag tone="amber">Absente de la dernière copie</Tag>}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 break-words">
                              <span className="font-mono">{f.code}</span>
                              {f.audience.length > 0 && <> · {roleLabels(catalog.roles, f.audience).join(", ")}</>}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <FeatureDialog catalog={catalog} editing={editing} canManage={canManage} onClose={() => setEditing(null)} />
      <SectionDialog section={renaming} onClose={() => setRenaming(null)} onSaved={catalog.reload} />
    </div>
  );
}

function draftOf(feature, sections) {
  if (feature === "new") return { label: "", description: "", section_code: sections[0]?.code || "", audience: [], route: "" };
  return { label: feature.label, description: feature.description || "", section_code: feature.section_code, audience: feature.audience || [], route: feature.route || "" };
}

// Creation, consultation and correction of one feature. Read-only without the right to manage the catalogue.
function FeatureDialog({ catalog, editing, canManage, onClose }) {
  const isNew = editing === "new";
  const feature = isNew ? null : editing;
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  if (editing && openFor !== editing) {
    setOpenFor(editing);
    setDraft(draftOf(editing, catalog.sections));
  }
  const readOnly = !canManage;
  const fromJamora = feature?.source === "jamora";

  const send = async (request, success) => {
    setBusy(true);
    try {
      await request();
      toast.success(success);
      await catalog.reload();
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.msg || errorMessage(error));
      if (detail?.code === "version_conflict") await catalog.reload();
    } finally {
      setBusy(false);
    }
  };

  const patch = feature && draft ? buildFeaturePatch(feature, draft) : null;
  const valid = draft && draft.label.trim().length >= 2 && draft.section_code;
  const save = () => (isNew
    ? send(() => api.post("/training/features", {
      section_code: draft.section_code, label: draft.label.trim(), description: draft.description.trim() || null,
      route: draft.route.trim() || null, audience: draft.audience,
    }), "Fonctionnalité ajoutée")
    : send(() => api.patch(`/training/features/${encodeURIComponent(feature.code)}`, patch), "Fonctionnalité mise à jour"));
  const setStatus = (next) => send(() => api.patch(`/training/features/${encodeURIComponent(feature.code)}`, { expected_version: feature.version, status: next }),
    next === "retired" ? "Fonctionnalité retirée" : "Fonctionnalité réactivée");
  const toggleRole = (code) => setDraft((d) => ({ ...d, audience: d.audience.includes(code) ? d.audience.filter((c) => c !== code) : [...d.audience, code] }));

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="feature-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nouvelle fonctionnalité" : readOnly ? feature?.label : "Modifier la fonctionnalité"}</DialogTitle>
          <DialogDescription>
            {isNew ? "Une fonctionnalité propre au CRM, à côté de celles copiées de Jamora."
              : <>Code <span className="font-mono">{feature?.code}</span> · {fromJamora ? "copiée de Jamora" : "ajoutée dans le CRM"}{feature?.status === "retired" ? " · retirée" : ""}</>}
          </DialogDescription>
        </DialogHeader>
        {draft && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ft-label">Libellé *</Label>
              <Input id="ft-label" className="text-base md:text-sm" maxLength={160} value={draft.label} disabled={readOnly}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="input-feature-label" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ft-section">Rubrique *</Label>
              <Select value={draft.section_code} onValueChange={(v) => setDraft({ ...draft, section_code: v })} disabled={readOnly}>
                <SelectTrigger id="ft-section" data-testid="select-feature-section"><SelectValue placeholder="Choisir une rubrique" /></SelectTrigger>
                <SelectContent>{catalog.sections.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ft-desc">Description</Label>
              <Textarea id="ft-desc" rows={3} maxLength={2000} className="text-base md:text-sm" value={draft.description} disabled={readOnly}
                placeholder="Ce que la personne sait faire dans Jamora avec cette fonctionnalité"
                onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="input-feature-description" />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-900">Rôles pharmacie visés</legend>
              <p className="text-xs text-slate-500">{fromJamora ? "Calculés depuis les droits de Jamora lors de la copie ; vous pouvez les ajuster." : "Les personnes de la pharmacie concernées par cette fonctionnalité."}</p>
              <div className="grid grid-cols-2 gap-1">
                {catalog.roles.map((r) => (
                  <label key={r.code} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                    <Checkbox checked={draft.audience.includes(r.code)} onCheckedChange={() => toggleRole(r.code)} disabled={readOnly}
                      data-testid={`check-feature-role-${r.code}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            </fieldset>
            {fromJamora ? (
              (feature.route || feature.required_pages.length > 0) && (
                <div className="text-xs text-slate-500 space-y-0.5">
                  {feature.route && <div>Page Jamora : <span className="font-mono">{feature.route}</span></div>}
                  {feature.required_pages.length > 0 && <div>Droits Jamora nécessaires : <span className="font-mono">{feature.required_pages.join(", ")}</span></div>}
                </div>
              )
            ) : (
              <div className="space-y-2">
                <Label htmlFor="ft-route">Page Jamora</Label>
                <Input id="ft-route" className="text-base md:text-sm" maxLength={200} value={draft.route} disabled={readOnly} placeholder="Ex. : /stocks"
                  onChange={(e) => setDraft({ ...draft, route: e.target.value })} />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0 sm:justify-between">
          {!isNew && canManage ? (
            <Button variant="outline" onClick={() => setStatus(feature.status === "retired" ? "active" : "retired")} disabled={busy}
              data-testid="btn-feature-status">
              {feature?.status === "retired" ? "Réactiver" : "Retirer"}
            </Button>
          ) : <span />}
          <div className="flex gap-2 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={onClose} disabled={busy}>{readOnly ? "Fermer" : "Annuler"}</Button>
            {!readOnly && (
              <Button onClick={save} disabled={!valid || busy || (!isNew && (!patch || isPatchEmpty(patch)))} data-testid="btn-feature-save">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionDialog({ section, onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  if (section && openFor !== section) {
    setOpenFor(section);
    setLabel(section.label);
  }
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/training/sections/${encodeURIComponent(section.code)}`, { expected_version: section.version, label: label.trim() });
      toast.success("Rubrique renommée");
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.msg || errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!section} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="section-dialog">
        <DialogHeader>
          <DialogTitle>Renommer la rubrique</DialogTitle>
          <DialogDescription>Son code ne change pas : les fonctionnalités et les formations qui s'y rattachent restent liées.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="sec-label">Libellé *</Label>
          <Input id="sec-label" className="text-base md:text-sm" maxLength={160} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="input-section-label" />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={save} disabled={label.trim().length < 2 || busy || label.trim() === section?.label} data-testid="btn-section-save">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
